// src/contexts/AuthContext.js
import { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";
import LoadingIndicator from "../components/LoadingIndicator";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [userCompanyId, setUserCompanyId] = useState(null);
  const [companyAccountUid, setCompanyAccountUid] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);

  const loadStoredCompany = () => {
    const storedCompany =
      localStorage.getItem("selectedCompany") ||
      sessionStorage.getItem("selectedCompany");

    if (storedCompany) {
      try {
        const parsedCompany = JSON.parse(storedCompany);
        setSelectedCompany(parsedCompany);
        return parsedCompany;
      } catch (error) {
        console.error("Fel vid parsning av sparat företag:", error);
      }
    }

    return null;
  };

  const clearCompanyData = () => {
    setCompany(null);
    setUserRole(null);
    setUserCompanyId(null);
    setCompanyAccountUid(null);
    setSelectedCompany(null);

    const keys = [
      "selectedCompany",
      "currentCompany",
      "selectedCompanyId",
      "companyId",
      "companyAccountUid",
      "company",
      "activeCompany",
      "activeCompanyId",
    ];

    keys.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  };

  const applySelectedCompanyToState = (user, storedCompany) => {
    if (!user || !storedCompany) return;

    const resolvedCompanyAccountUid =
      storedCompany.companyAccountUid ||
      storedCompany.uid ||
      storedCompany.companyId ||
      null;

    setSelectedCompany(storedCompany);
    setUserRole(storedCompany.role || null);
    setUserCompanyId(storedCompany.companyId || null);
    setCompanyAccountUid(resolvedCompanyAccountUid);

    setProfile({
      id: user.uid,
      uid: user.uid,
      email: user.email || storedCompany.email || "",
      role: storedCompany.role || null,
      companyId: storedCompany.companyId || null,
      companyName: storedCompany.companyName || "Företag",
      companyAccountUid: resolvedCompanyAccountUid,
      isEmployedAdmin: storedCompany.isEmployedAdmin || false,
      employedDocId: storedCompany.employedDocId || null,
      employedData: storedCompany.employedData || null,
      userData: storedCompany.userData || null,
    });

    setCompany({
      id: storedCompany.companyId || resolvedCompanyAccountUid,
      name: storedCompany.companyName || "Företag",
      companyId: storedCompany.companyId || null,
      companyName: storedCompany.companyName || "Företag",
      accountUid: resolvedCompanyAccountUid,
      companyAccountUid: resolvedCompanyAccountUid,
      email: storedCompany.email || user.email || "",
      role: storedCompany.role || "admin",
      ...storedCompany.userData,
    });
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("🔐 Auth state changed:", user?.uid || "ingen användare");
      setCurrentUser(user);

      if (!user) {
        clearCompanyData();
        setProfile(null);
        setLoading(false);
        setIsInitializing(false);
        setAuthInitialized(true);
        return;
      }

      try {
        setLoading(true);
        setIsInitializing(true);

        const storedCompany = loadStoredCompany();

        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          console.warn(
            "⚠️ Ingen top-level användarprofil hittades. Försöker använda sparat företag/employed-admin."
          );

          if (storedCompany?.role) {
            applySelectedCompanyToState(user, storedCompany);
          } else {
            setProfile(null);
          }

          return;
        }

        const userData = userSnap.data();

        const resolvedCompanyAccountUid =
          userData.companyAccountUid ||
          userData.companyUserId ||
          user.uid;

        const resolvedCompanyId = userData.companyId || null;
        const resolvedRole = userData.role || null;

        setProfile({
          id: user.uid,
          ...userData,
          companyAccountUid: resolvedCompanyAccountUid,
        });

        setUserRole(resolvedRole);
        setUserCompanyId(resolvedCompanyId);
        setCompanyAccountUid(resolvedCompanyAccountUid);

        if (storedCompany?.role) {
          applySelectedCompanyToState(user, storedCompany);
          return;
        }

        const companyAccountRef = doc(db, "users", resolvedCompanyAccountUid);
        const companyAccountSnap = await getDoc(companyAccountRef);

        if (companyAccountSnap.exists()) {
          const companyAccountData = companyAccountSnap.data();

          setCompany({
            id:
              companyAccountData.companyId ||
              userData.companyId ||
              resolvedCompanyAccountUid,
            name:
              companyAccountData.companyName ||
              userData.companyName ||
              "Företag",
            companyId:
              companyAccountData.companyId || userData.companyId || null,
            companyName:
              companyAccountData.companyName ||
              userData.companyName ||
              "Företag",
            accountUid: resolvedCompanyAccountUid,
            companyAccountUid: resolvedCompanyAccountUid,
            email: companyAccountData.email || "",
            role: companyAccountData.role || userData.role || "admin",
            ...companyAccountData,
          });
        } else {
          setCompany({
            id: userData.companyId || user.uid,
            name: userData.companyName || "Företag",
            companyId: userData.companyId || null,
            companyName: userData.companyName || "Företag",
            accountUid: resolvedCompanyAccountUid,
            companyAccountUid: resolvedCompanyAccountUid,
            email: userData.email || user.email || "",
            role: userData.role || "admin",
            ...userData,
          });
        }
      } catch (error) {
        console.error("❌ Fel vid hämtning av användarprofil:", error);
        setProfile(null);
      } finally {
        setLoading(false);
        setIsInitializing(false);
        setAuthInitialized(true);
      }
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email, password, remember = false) => {
    try {
      console.log("🔐 Försöker logga in:", email);

      clearCompanyData();

      await setPersistence(
        auth,
        remember ? browserLocalPersistence : browserSessionPersistence
      );

      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      const user = userCredential.user;

      console.log("✅ Firebase auth lyckades:", user.uid);

      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        console.warn(
          "⚠️ Top-level profil saknas, men login tillåts. LoginPage får kontrollera employed-admin."
        );

        return userCredential;
      }

      const userData = userSnap.data();

      const resolvedCompanyAccountUid =
        userData.companyAccountUid || userData.companyUserId || user.uid;

      await setDoc(
        userRef,
        {
          lastLoginAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastLoginEmail: email,
          companyAccountUid: resolvedCompanyAccountUid,
        },
        { merge: true }
      );

      console.log("✅ Inloggning godkänd");
      return userCredential;
    } catch (error) {
      console.error("❌ Inloggningsfel:", error);
      clearCompanyData();
      throw error;
    }
  };

  const signOut = async () => {
    try {
      clearCompanyData();
      setProfile(null);
      setCurrentUser(null);

      await firebaseSignOut(auth);
      console.log("👋 Användare utloggad");
    } catch (error) {
      console.error("❌ Utloggningsfel:", error);
      throw error;
    }
  };

  const accountUidToUse =
    selectedCompany?.companyAccountUid ||
    companyAccountUid ||
    currentUser?.uid ||
    null;

  const currentRole = selectedCompany?.role || userRole;
  const currentCompanyId =
    selectedCompany?.companyId || userCompanyId || company?.companyId;

  if (!authInitialized && loading) {
    return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}><LoadingIndicator message="Laddar..." fullPage /></div>;
  }

  const value = {
    currentUser,
    profile,
    company,
    companyId: currentCompanyId,
    companyAccountUid: accountUidToUse,
    accountUidToUse,
    role: currentRole,
    userRole: currentRole,
    isAdmin:
      currentRole === "admin" ||
      currentRole === "super-admin" ||
      currentRole === "superadmin",
    isSuperAdmin:
      currentRole === "super-admin" || currentRole === "superadmin",
    isAuthenticated:
      !!currentUser &&
      (currentRole === "admin" ||
        currentRole === "super-admin" ||
        currentRole === "superadmin"),
    loading: loading || isInitializing,
    isInitializing,
    signIn,
    signOut,
    logout: signOut,
    selectedCompany,
    setSelectedCompany,
    clearCompanyData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
